import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DISCIPLINE_LABELS = {
  kickboxing: "Kickboxing / Muay Thaï",
  "muay-thai": "Kickboxing / Muay Thaï",
  "boxe-anglaise": "Boxe anglaise",
  bjj: "Jiu-Jitsu Brésilien",
  jjj: "Jiu-Jitsu Can Ryu",
  mma: "MMA",
  karate: "Karaté",
  savate: "Savate",
  autre: "Autre",
};
const INTEREST_LABELS = {
  gala_vi: "Gala VI 2027",
  stages: "Stages Kru Ralph",
  collaboration: "Collaboration",
  plateforme: "Plateforme Formation",
};

/**
 * Dashboard admin Réseau Coachs Gladiateurs (POC · non protégé pour MVP).
 * URL : /plateforme/reseau-coachs/admin
 */
export default function PlateformeReseauCoachsAdmin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    document.title = "Admin · Réseau Coachs Gladiateurs";
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/plateforme/reseau-coachs/stats`);
      setStats(data);
      setError("");
    } catch (e) {
      setError(e.message || "Erreur chargement stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(fetchStats, 15000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const baseStyle = {
    fontFamily: "Inter, system-ui, sans-serif",
    background: "#0d0d0d",
    color: "#f5f5f5",
    minHeight: "100vh",
  };

  if (loading) {
    return (
      <div style={baseStyle} className="flex items-center justify-center" data-testid="loading">
        <div className="text-white/60">Chargement...</div>
      </div>
    );
  }

  return (
    <div style={baseStyle} data-testid="admin-reseau-coachs">
      <style>{`
        .editorial { font-family: 'Playfair Display', serif; }
        .label { letter-spacing: 0.4em; font-size: 11px; }
        .gold { color: #C9A961; }
        .red-c { color: #C8102E; }
        .bg-red-c { background: #C8102E; }
        .bg-gold { background: #C9A961; }
        .card {
          background: #161616;
          border: 1px solid rgba(201, 169, 97, 0.25);
          padding: 24px;
          transition: 0.3s;
        }
        .card:hover { border-color: #C9A961; }
        .divider-gold { background: linear-gradient(90deg, transparent, #C9A961 30%, #C9A961 70%, transparent); height: 1px; }
        .bar-track { background: rgba(255,255,255,0.06); height: 8px; position: relative; overflow: hidden; }
        .bar-fill { background: linear-gradient(90deg, #C8102E, #C9A961); height: 100%; transition: width 0.4s; }
        table { border-collapse: collapse; width: 100%; }
        th, td { padding: 10px 12px; text-align: left; font-size: 13px; border-bottom: 1px solid rgba(255,255,255,0.06); }
        th { color: #C9A961; letter-spacing: 0.2em; font-size: 10px; text-transform: uppercase; }
        tr:hover td { background: rgba(201, 169, 97, 0.04); }
        .btn-ghost { color: #C9A961; border: 1px solid rgba(201, 169, 97, 0.4); padding: 8px 16px; font-size: 12px; cursor: pointer; transition: 0.2s; background: transparent; letter-spacing: 0.2em; text-transform: uppercase; }
        .btn-ghost:hover { background: #C9A961; color: #0d0d0d; }
        .pulse {
          display: inline-block; width: 8px; height: 8px; border-radius: 50%;
          background: #10B981; animation: pulse 1.6s infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>

      <nav
        className="px-10 py-6 flex items-center justify-between"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
      >
        <Link to="/plateforme" className="flex items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 bg-red-c flex items-center justify-center">
              <span className="editorial text-white text-2xl font-black">I</span>
            </div>
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-gold"></div>
          </div>
          <div>
            <div className="label gold">— ADMIN DASHBOARD —</div>
            <div className="editorial text-2xl font-bold leading-none mt-0.5">
              Réseau Coachs Gladiateurs
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-4">
          <label className="text-xs text-white/60 flex items-center gap-2" data-testid="toggle-refresh">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span className="pulse"></span>
            Auto-refresh (15s)
          </label>
          <button onClick={fetchStats} className="btn-ghost" data-testid="btn-refresh">
            Refresh
          </button>
          <Link to="/plateforme/reseau-coachs" className="btn-ghost" data-testid="link-public">
            Page publique →
          </Link>
        </div>
      </nav>

      <main className="px-10 py-10 max-w-7xl mx-auto">
        {error && (
          <div className="card mb-6" style={{ borderColor: "#C8102E", background: "#3b0d0d" }} data-testid="error">
            ⚠️ {error}
          </div>
        )}

        {/* KPIs */}
        <section className="grid md:grid-cols-4 gap-6 mb-10">
          <div className="card" data-testid="kpi-total">
            <div className="label gold mb-3">— TOTAL INSCRITS —</div>
            <div className="editorial text-6xl font-black gold">{stats.total}</div>
            <div className="text-xs text-white/40 mt-2">coachs partenaires</div>
          </div>
          <div className="card" data-testid="kpi-new">
            <div className="label red-c mb-3">— NOUVEAUX —</div>
            <div className="editorial text-6xl font-black red-c">{stats.new_count}</div>
            <div className="text-xs text-white/40 mt-2">à traiter / recontacter</div>
          </div>
          <div className="card" data-testid="kpi-clubs">
            <div className="label gold mb-3">— CLUBS UNIQUES —</div>
            <div className="editorial text-6xl font-black">
              {Object.keys(stats.by_club).length}
            </div>
            <div className="text-xs text-white/40 mt-2">organisations distinctes</div>
          </div>
          <div className="card" data-testid="kpi-provinces">
            <div className="label gold mb-3">— PROVINCES/PAYS —</div>
            <div className="editorial text-6xl font-black">
              {Object.keys(stats.by_province).length}
            </div>
            <div className="text-xs text-white/40 mt-2">zones géographiques</div>
          </div>
        </section>

        {/* Disciplines + Intérêts */}
        <section className="grid md:grid-cols-2 gap-6 mb-10">
          <div className="card" data-testid="block-disciplines">
            <div className="label gold mb-6">— DISCIPLINES ENSEIGNÉES —</div>
            {Object.keys(stats.by_discipline).length === 0 ? (
              <div className="text-white/40 text-sm">Aucune donnée pour le moment.</div>
            ) : (
              Object.entries(stats.by_discipline).map(([k, v]) => {
                const max = Math.max(...Object.values(stats.by_discipline));
                const pct = (v / max) * 100;
                return (
                  <div key={k} className="mb-4">
                    <div className="flex justify-between mb-1 text-sm">
                      <span>{DISCIPLINE_LABELS[k] || k}</span>
                      <span className="gold font-bold">{v}</span>
                    </div>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="card" data-testid="block-interests">
            <div className="label gold mb-6">— INTÉRÊTS DÉCLARÉS —</div>
            {Object.keys(stats.by_interest).length === 0 ? (
              <div className="text-white/40 text-sm">Aucune donnée pour le moment.</div>
            ) : (
              Object.entries(stats.by_interest).map(([k, v]) => {
                const max = Math.max(...Object.values(stats.by_interest));
                const pct = (v / max) * 100;
                return (
                  <div key={k} className="mb-4">
                    <div className="flex justify-between mb-1 text-sm">
                      <span>{INTEREST_LABELS[k] || k}</span>
                      <span className="gold font-bold">{v}</span>
                    </div>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Clubs + Géo */}
        <section className="grid md:grid-cols-2 gap-6 mb-10">
          <div className="card" data-testid="block-clubs">
            <div className="label gold mb-6">— TOP CLUBS —</div>
            {Object.keys(stats.by_club).length === 0 ? (
              <div className="text-white/40 text-sm">Aucun club pour le moment.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Club</th>
                    <th style={{ textAlign: "right" }}>Coachs</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(stats.by_club).map(([k, v]) => (
                    <tr key={k}>
                      <td>{k}</td>
                      <td style={{ textAlign: "right" }} className="gold font-bold">
                        {v}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card" data-testid="block-provinces">
            <div className="label gold mb-6">— RÉPARTITION GÉO —</div>
            {Object.keys(stats.by_province).length === 0 ? (
              <div className="text-white/40 text-sm">Aucune province/pays pour le moment.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Province / Pays</th>
                    <th style={{ textAlign: "right" }}>Coachs</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(stats.by_province).map(([k, v]) => (
                    <tr key={k}>
                      <td>{k}</td>
                      <td style={{ textAlign: "right" }} className="gold font-bold">
                        {v}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Liste détaillée */}
        <section className="card" data-testid="block-leads">
          <div className="label gold mb-6">— DERNIERS INSCRITS (50 max) —</div>
          {stats.recent_leads.length === 0 ? (
            <div className="text-white/40 text-sm">
              Aucun coach inscrit pour le moment. Les inscriptions apparaîtront ici en temps
              réel pendant le Gala.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Nom</th>
                    <th>Club</th>
                    <th>Ville · Province/Pays</th>
                    <th>Email</th>
                    <th>Disciplines</th>
                    <th>Intérêts</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                      <td className="text-white/50">
                        {new Date(l.created_at).toLocaleString("fr-CA", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </td>
                      <td>
                        <div className="font-bold">{l.full_name}</div>
                        {l.phone && (
                          <div className="text-xs text-white/40">{l.phone}</div>
                        )}
                      </td>
                      <td>{l.club_name}</td>
                      <td className="text-white/60">
                        {[l.city, l.province_country].filter(Boolean).join(" · ")}
                      </td>
                      <td className="gold">{l.email}</td>
                      <td className="text-xs">
                        {l.disciplines
                          .map((d) => DISCIPLINE_LABELS[d] || d)
                          .join(", ")}
                      </td>
                      <td className="text-xs">
                        {l.interested_in
                          .map((i) => INTEREST_LABELS[i] || i)
                          .join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      <footer
        className="px-10 py-8"
        style={{ borderTop: "1px solid rgba(201,169,97,0.2)" }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <div className="divider-gold w-32 mx-auto mb-3"></div>
          <div className="text-xs text-white/40 tracking-widest">
            © 2026 — DASHBOARD ADMIN · RÉSEAU COACHS GLADIATEURS · POC
          </div>
        </div>
      </footer>
    </div>
  );
}
